import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  Input,
  ChangeDetectorRef,
  Output,
  EventEmitter,
  OnDestroy,
} from '@angular/core';
import {
  ReactiveFormsModule,
  FormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  UntypedFormControl,
} from '@angular/forms';
import { ClientTrialService, TimeconversionService, TrialService, ClientService } from '@fitness-force/api';
import { Status } from '@fitness-force/enum';
import { ErrorMsgComponent } from '@fitness-force/errors';
import {
  CreateClientTrial,
  TrialMaster,
} from '@fitness-force/models';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastMessageService } from '@fitness-force/api';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { PopoverModule } from 'primeng/popover';
import { RippleModule } from 'primeng/ripple';
import { SkeletonModule } from 'primeng/skeleton';
import { StyleClassModule } from 'primeng/styleclass';
import { AssignedToComponent } from '../staff/assigned-to/assigned-to.component';
import { Subscription, of, debounceTime, distinctUntilChanged, switchMap, catchError } from 'rxjs';
import Statsig from 'statsig-js';

@Component({
  selector: 'create-client-trial',
  templateUrl: './create-client-trial.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ToastMessageService],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    ButtonModule,
    RippleModule,
    DialogModule,
    DatePickerModule,
    SkeletonModule,
    PopoverModule,
    StyleClassModule,
    ErrorMsgComponent,
    AssignedToComponent,
    TranslateModule,
    InputTextModule
  ]
})
export class CreateClientTrialComponent implements OnInit, OnDestroy {
  @Input('tenantId') tenantId: number = 0;
  @Input('clientId') clientId: number = 0;
  @Input('keyfob') clientKeyfob: string = '';
  @Input('visible') visible: boolean = false;

  @Output('closeEvent') closeEvent: EventEmitter<any> =new EventEmitter();

  trialMaster: TrialMaster | undefined;
  minimumDate = new Date();
  clientTrialForm: UntypedFormGroup = new UntypedFormGroup({});
  showDaysBased : boolean= false;
  showClassBased :boolean= false;

  isFormSubmitting = false;

  trialEndDate = new Date();

  // Keyfob related properties
  digitalKeyFOBSelected: boolean = false;
  physicalKeyFOBSelected: boolean = true;
  keyFobRequired: boolean = true;
  validAlphaNumericPattern = "^[a-zA-Z0-9]*$";
  isValidatingKeyfob: boolean = false;
  isKeyfobValid: boolean = false;
  keyfobValidationMessage: string = '';
  showDigitalKeyFob: boolean = false;
  private keyfobValidationSubscription!: Subscription;

  constructor(
    private clientTrialService: ClientTrialService,
    private fb: UntypedFormBuilder,
    private cd: ChangeDetectorRef,
    private trialMasterService: TrialService,
    public translate: TranslateService,
    private toastMessageService: ToastMessageService,
    private timeService: TimeconversionService,
    private clientService: ClientService
  ) {}

  ngOnInit(): void {
    if (Statsig.checkGate("keyfob")) {
      this.keyFobRequired = true;
    } else {
      this.keyFobRequired = false;
    }

    if (Statsig.checkGate("show_digital_keyfob")) {
      this.showDigitalKeyFob = true;
    } else {
      this.showDigitalKeyFob = false;
      this.physicalKeyFOBSelected = true;
      this.digitalKeyFOBSelected = false;
    }

    this.getTrialMaster();
    
    // Build validators array for keyfob, filtering out null values
    const keyfobValidators = [
      this.keyFobRequired ? Validators.required : null,
      Validators.pattern(this.validAlphaNumericPattern)
    ].filter(validator => validator !== null);

    this.clientTrialForm = this.fb.group({
      startDate: [this.minimumDate, Validators.required],
      trialOwner: [null, Validators.required],
      keyfob: [this.clientKeyfob, keyfobValidators],
    });

    // Set up keyfob validation listener only if keyfob is required
    if (this.keyFobRequired) {
      this.setupKeyfobValidation();
    }
  }

  ngOnDestroy(): void {
    if (this.keyfobValidationSubscription) {
      this.keyfobValidationSubscription.unsubscribe();
    }
  }

  setupKeyfobValidation(): void {
    if (!this.keyFobFormControl) {
      return; // Exit early if form control doesn't exist yet
    }
    
    this.keyfobValidationSubscription = this.keyFobFormControl.valueChanges.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      switchMap((value: string) => {
        if (value && value.trim() && value !== this.clientKeyfob && this.tenantId > 0) {
          this.isValidatingKeyfob = true;
          this.keyfobValidationMessage = '';
          this.cd.detectChanges();
          return this.clientService.validateKeyfob(this.tenantId, value.trim()).pipe(
            catchError((error: any) => {
              this.isValidatingKeyfob = false;
              this.isKeyfobValid = false;
              if (error?.originalError?.error?.errorMessage) {
                this.keyfobValidationMessage = error.originalError.error.errorMessage;
              } else {
                this.keyfobValidationMessage = 'Invalid keyfob number';
              }
              this.cd.detectChanges();
              return of(null);
            })
          );
        } else {
          this.isValidatingKeyfob = false;
          this.isKeyfobValid = false;
          this.keyfobValidationMessage = '';
          this.cd.detectChanges();
          return of(null);
        }
      })
    ).subscribe((response) => {
      if (response !== null) {
        this.isValidatingKeyfob = false;
        this.isKeyfobValid = true;
        this.keyfobValidationMessage = 'Keyfob is valid';
        this.cd.detectChanges();
      }
    });
  }

  toggleKeyFobSelectionType(digitalKeyFOBSelected: boolean, physicalKeyFOBSelected: boolean): void {
    this.digitalKeyFOBSelected = digitalKeyFOBSelected;
    this.physicalKeyFOBSelected = physicalKeyFOBSelected;
    
    if (!this.keyFobFormControl) {
      return; // Exit early if form control doesn't exist yet
    }
    
    if (this.physicalKeyFOBSelected) {
      this.keyFobFormControl.addValidators(Validators.required);
      this.keyFobFormControl.addValidators(Validators.pattern(this.validAlphaNumericPattern));
    }
    else {
      this.keyFobFormControl.removeValidators(Validators.required);
      this.keyFobFormControl.removeValidators(Validators.pattern(this.validAlphaNumericPattern));
      this.keyFobFormControl.clearValidators();
      this.keyFobFormControl.setErrors(null);
    }
    this.keyFobFormControl.updateValueAndValidity();
    this.cd.detectChanges();
  }

  getTrialMaster() {
    this.trialMasterService.getTrials(this.tenantId).subscribe({
      next: (response: any) => {
        if (response && response.items) {
          let trialMasters = response.items as TrialMaster[];
          this.trialMaster = trialMasters.find(o => o.status == Status.Active || o.status.toString() == Status[Status.Active]);
          this.trialEndDate = new Date(this.trialEndDate.setDate(this.trialEndDate.getDate() + this.trialMaster!.max_trial_validity -1));
          this.showDaysBased = true;
        }
      },
      error: (error) => {
        this.handleError(error);
        this.trialMaster = undefined;
      },
    });
  }

  saveTrialForm() {
    if (this.clientTrialForm.valid) {
      // Check keyfob validation
      if (this.keyFobRequired && this.physicalKeyFOBSelected && this.keyFobFormControl?.invalid) {
        this.keyFobFormControl?.markAsDirty();
        this.cd.detectChanges();
        return;
      }

      const data = { ...this.clientTrialForm.getRawValue() };
      let createClientTrial: CreateClientTrial & { keyfob_type?: string; keyfob_number?: string | null } = {} as CreateClientTrial & { keyfob_type?: string; keyfob_number?: string | null };
      var startDateTime = new Date(new Date(data.startDate).setHours(0,0,0,0));
      createClientTrial.start_date = this.timeService.ConvertLocalDateTimeToUTC(startDateTime);
      createClientTrial.opportunity_id = 0;
      createClientTrial.owner_id = data.trialOwner.id;
      createClientTrial.trial_master_id = this.trialMaster!.id;
      
      // Add keyfob data to payload
      if (this.digitalKeyFOBSelected) {
        createClientTrial.is_digital_keyfob = true;
        createClientTrial.keyfob_number = '';
      } else if (this.physicalKeyFOBSelected && data.keyfob) {
        createClientTrial.is_digital_keyfob = false;
        createClientTrial.keyfob_number = data.keyfob;
      }
      else {
        createClientTrial.is_digital_keyfob = false;
        createClientTrial.keyfob_number = '';
      }

      this.isFormSubmitting = true;
      this.clientTrialService.CreateClientTrial(this.tenantId,this.clientId, createClientTrial).subscribe({
        next: (res: any) => {
          this.isFormSubmitting = false;
          this.translate.get('CLIENT_TRIAL.MSG_SUCCESS')
            .subscribe((translation) => {
              this.toastMessageService.showSuccess(translation);
            });
            this.closePopup(true);
        },
        error: (error: any) => {
          this.isFormSubmitting = false;
          this.handleError(error);
        },
      });
    } else {
      // Todo Fire all control touches ...
      Object.keys(this.clientTrialForm.controls).forEach((field) => {
        const control = this.clientTrialForm.get(field);
        control?.markAsTouched({ onlySelf: true });
      });
      this.cd.detectChanges();
    }
  }

  handleError(error: any) {
    if (error.originalError.error.length > 0) {
      error.originalError.error.forEach((e: any) => {
        if (e.errorCode.toLowerCase() == 'overlap') {
          this.showOverlapErrorMessage(e);
        } else {
          this.toastMessageService.showErrorMessage(e);
        }
      });
    } else {
      this.toastMessageService.showErrorMessage(error);
    }
  }

  showOverlapErrorMessage(error: any) {
    this.translate.get('SCHEDULE.MSG.OVERLAP_MESSAGE', {
        type: error.jsonObject.identifier,
        startDate: this.timeService.formatDate(
          this.timeService.ConvertUTCToLocalDateTime(
            error.jsonObject.startDateTime
          ),
          'medium'
        ),
        endDate: this.timeService.formatDate(
          this.timeService.ConvertUTCToLocalDateTime(
            error.jsonObject.endDateTime
          ),
          'medium'
        ),
      })
      .subscribe((translation) => {
        this.toastMessageService.showErrorMessage(translation);
      });
  }

  getEndDate(event:any) {
    const selectedDate = new Date(this.StartDateFormControl.value);
    this.trialEndDate= new Date(selectedDate.setDate(selectedDate.getDate() + this.trialMaster!.max_trial_validity -1));
  }

  closePopup(reloadData:boolean)
  {
    this.visible = false;
    this.showDaysBased = false;
    this.showClassBased = false;
    this.clientTrialForm.reset();
    this.closeEvent.emit({closeRoomPopup: true, reloadData:reloadData});
  }

  get StartDateFormControl(): UntypedFormControl {
    return this.clientTrialForm?.controls?.startDate as UntypedFormControl;
  }

  get trialOwnerFormControl(): UntypedFormControl {
    return this.clientTrialForm?.controls?.trialOwner as UntypedFormControl;
  }

  get keyFobFormControl(): UntypedFormControl {
    return this.clientTrialForm?.controls?.keyfob as UntypedFormControl;
  }

  onTrainerChange(event: any): void {
    // Handle trainer change if needed
  }
}
